import {
  IElement,
  IProductElement,
  ProductElementOverrideTarget,
  ProductElementOverrides,
} from '@shared/models/project.model';
import { ElementPropertyName } from '@shared/models/element-property.model';
import { ElementPropertyCountType } from '@shared/models/element-property.model';
import {
  ProjectChange,
  ProjectChangeType,
} from './pipeline/update-engine.models';
import { isElement, isProductElement } from './recursive-element.helpers';
import {
  getSelectedElementQuantityProperty,
  getSelectedQuantityName,
} from './element-quantity-core.helpers';
import {
  getElementProperty,
  setPropertyCountsFromRecord,
} from './element-property.helpers';
import { setProductOverride } from './product-element.helpers';
import { applyCategoryChildElements } from './element-category.helpers';

/**
 * Element properties the user owns (never re-imported), ported across a product
 * switch when the new element still defines them. The CSV import only writes
 * {@link ElementPropertyName.SbefCode} (imported); these stay the user's.
 */
const RETAINED_ELEMENT_PROPERTY_NAMES: readonly ElementPropertyName[] = [
  ElementPropertyName.Lifetime,
  ElementPropertyName.ThermalTransmittance,
  ElementPropertyName.ReusedContent,
  ElementPropertyName.ContentCoverage,
];

/**
 * Product-element overrides the user owns. {@link ProductElementOverrideTarget.WASTE_PERCENT}
 * is excluded because it is the imported "spill" — it always comes from the
 * freshly materialized element.
 */
const RETAINED_PRODUCT_OVERRIDE_TARGETS: readonly ProductElementOverrideTarget[] =
  [
    ProductElementOverrideTarget.CO2E_A1_A3,
    ProductElementOverrideTarget.SEK_A1_A3,
    ProductElementOverrideTarget.TRANSPORT_KM,
  ];

const findProductChild = (element: IElement): IProductElement | undefined =>
  element.elements.find(isProductElement);

/** Copy the user's non-waste product overrides from {@link existing} onto {@link merged}'s product child. */
const portProductOverrides = (
  existing: IElement,
  merged: IElement,
): IElement => {
  const existingOverrides = findProductChild(existing)?.productOverrides;
  if (!existingOverrides) return merged;

  const childIndex = merged.elements.findIndex(isProductElement);
  if (childIndex < 0) return merged;

  let child = merged.elements[childIndex] as IProductElement;
  let changed = false;
  for (const target of RETAINED_PRODUCT_OVERRIDE_TARGETS) {
    const override = existingOverrides[target];
    if (override === undefined) continue;
    child = setProductOverride(
      child,
      target,
      override as ProductElementOverrides[typeof target],
    );
    changed = true;
  }
  if (!changed) return merged;

  const elements = [...merged.elements];
  elements[childIndex] = child;
  return { ...merged, elements };
};

/**
 * Merge a freshly materialized element ({@link fresh}) onto the user's existing
 * imported element so a product switch keeps everything the user owns while the
 * imported fields (product, category, quantity, sbef, spill) come from `fresh`.
 *
 * Retains: element id + name, {@link RETAINED_ELEMENT_PROPERTY_NAMES} (when the
 * new element still defines them), `isDeactivated`, and the user's product
 * overrides except `WASTE_PERCENT`. The product child is generated up-front so
 * the overrides attach to a node that enrichment reuses by `product_id`.
 */
export const mergeReimportedElement = (
  existing: IElement,
  fresh: IElement,
): IElement => {
  // Generate the product child now so overrides have a node to attach to; the
  // ElementAdd enrichment reuses it (matched by product_id), keeping them.
  let merged = isElement(fresh) ? applyCategoryChildElements(fresh) : fresh;

  merged = {
    ...merged,
    id: existing.id,
    name: existing.name,
    fallbackName: existing.fallbackName,
    isDeactivated: existing.isDeactivated,
  };

  const retainedCounts: Record<string, ElementPropertyCountType> = {};
  for (const name of RETAINED_ELEMENT_PROPERTY_NAMES) {
    const count = getElementProperty(existing, name, false)?.count;
    if (count !== undefined) retainedCounts[name] = count;
  }
  merged = setPropertyCountsFromRecord(merged, retainedCounts, true);

  return portProductOverrides(existing, merged);
};

/**
 * In-place project changes that re-derive only the imported quantity (+ its
 * quantity row) and the origin mapping on an already-materialized element,
 * leaving the same element id and every user edit untouched. Used when the
 * product is unchanged (in-row quantity/unit edit, side-panel rate editor).
 *
 * {@link fresh} is the element produced by re-running materialization with the
 * new mapping overlay; only its origin + selected quantity + count are applied.
 * The previously selected quantity's import count is cleared when the unit pick
 * moved the value to a different quantity row.
 */
export const buildReimportChanges = (params: {
  existing: IElement;
  fresh: IElement;
}): ProjectChange[] => {
  const { existing, fresh } = params;
  const changes: ProjectChange[] = [];

  if (fresh.origin) {
    changes.push({
      type: ProjectChangeType.ElementOrigin,
      elementId: existing.id,
      origin: fresh.origin,
    });
  }

  const freshSelected = getSelectedElementQuantityProperty(fresh);
  if (!freshSelected) return changes;

  const newName = freshSelected.name;
  const previousName = getSelectedQuantityName(existing);

  // Clear the stale import count from the old quantity row when the unit pick
  // moved the value to a different row (its fallbackCount takes over again).
  if (previousName && previousName !== newName) {
    changes.push({
      type: ProjectChangeType.ElementQuantityCount,
      elementId: existing.id,
      quantityName: previousName,
      count: undefined,
    });
  }

  changes.push({
    type: ProjectChangeType.ElementQuantitySelect,
    elementId: existing.id,
    quantityName: newName,
  });
  changes.push({
    type: ProjectChangeType.ElementQuantityCount,
    elementId: existing.id,
    quantityName: newName,
    count: freshSelected.count,
  });

  return changes;
};
